// Recent runs of a saved App's functions, for its editors: one quiet line each, newest first. A
// failed or unconfirmed run opens its log (what it printed, its error and its input) and offers Fix
// in chat, which opens the App's own chat with a draft of those facts for the person to send.
import { useCallback, useEffect, useState } from 'react';
import { ChevronDownIcon, MessageSquareIcon } from 'lucide-react';
import { useAppCatalogLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import appTrackerCopy from '@/i18n/ns/app-tracker';
import { Button } from '@/components/ui/button';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import { InlineError } from '../../primitives';
import { useOptionalWorkspace } from '../../context';
import { queueAppChatDraft } from '../app-chat-draft';
import { openAppChat } from '../open-app-chat';
import {
  createAppRunsApi,
  RunsNotAvailableToViewer,
  type AppRunsApi,
  type RunDetail,
  type RunSummary,
} from '../runs-api';
import { buildFixPrompt } from './run-fix-prompt';
import { formatMoment } from './schedule-words';

type Detail = RunDetail | 'loading' | 'failed';
const OUTCOME_COPY = {
  succeeded: 'runSucceeded',
  failed: 'runFailed',
  unknown: 'runUnknown',
} as const;
const TRIGGER_COPY = {
  manual: 'runManual',
  schedule: 'runScheduled',
  webhook: 'runWebhook',
  event: 'runEvent',
} as const;

function useRuns(api: AppRunsApi) {
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'hidden' | 'unavailable'>('loading');
  const load = useCallback(
    async (cursor: string | null) => {
      try {
        const page = await api.list(cursor);
        setRuns((current) => (cursor ? [...current, ...page.runs] : page.runs));
        setNext(page.nextCursor);
        setStatus('ready');
      } catch (error) {
        // Not an editor: no section at all. Anything else is said, never an empty history.
        setStatus(error instanceof RunsNotAvailableToViewer ? 'hidden' : 'unavailable');
      }
    },
    [api],
  );
  useEffect(() => {
    void load(null);
  }, [load]);
  return { runs, next, status, load };
}

export function AppRuns(props: {
  artifactId: string;
  versionId: string;
  /** The selected version's revision: Fix in chat opens this exact version's chat. */
  revisionId?: string;
  api?: AppRunsApi;
  /** Tests: where the Fix in chat draft goes instead of the App's chat. */
  onFixInChat?: (prompt: string) => Promise<void>;
}) {
  const copy = useNamespaceCopy(appTrackerCopy);
  const locale = useAppCatalogLocale();
  const bound = useSessionBoundFetch();
  const [own] = useState(() => createAppRunsApi(props.artifactId, props.versionId, bound));
  const api = props.api ?? own;
  const workspace = useOptionalWorkspace();
  const { runs, next, status, load } = useRuns(api);
  const [details, setDetails] = useState<Record<string, Detail>>({});
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [fixFailed, setFixFailed] = useState(false);
  const fixTo =
    props.onFixInChat ??
    (workspace && props.revisionId
      ? async (prompt: string) => {
          const { threadId } = await openAppChat({
            artifactId: props.artifactId,
            versionId: props.versionId,
            revisionId: props.revisionId as string,
          });
          // The App's chat opens by navigation and takes the draft when it is the open chat.
          queueAppChatDraft(threadId, prompt);
        }
      : null);
  const detailOf = async (run: RunSummary) => {
    const known = details[run.id];
    if (known && known !== 'loading' && known !== 'failed') return known;
    setDetails((current) => ({ ...current, [run.id]: 'loading' }));
    try {
      const detail = await api.detail(run.id);
      setDetails((current) => ({ ...current, [run.id]: detail }));
      return detail;
    } catch {
      setDetails((current) => ({ ...current, [run.id]: 'failed' }));
      return null;
    }
  };
  const toggle = async (run: RunSummary) => {
    const opening = !open[run.id];
    setOpen((current) => ({ ...current, [run.id]: opening }));
    if (opening) await detailOf(run);
  };
  const fix = async (run: RunSummary) => {
    if (!fixTo) return;
    setFixFailed(false);
    const detail = await detailOf(run);
    if (!detail) return;
    try {
      await fixTo(
        buildFixPrompt(detail, copy, {
          locale,
          when: formatMoment(detail.finishedAt, locale),
        }),
      );
    } catch {
      setFixFailed(true);
    }
  };
  if (status === 'hidden' || status === 'loading') return null;
  const seconds = (ms: number) => {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'unit',
        unit: 'second',
        maximumFractionDigits: 1,
      }).format(ms / 1000);
    } catch {
      return `${(ms / 1000).toFixed(1)} s`;
    }
  };
  return (
    <section className="pc-app-runs" data-testid="app-runs">
      <h3>{copy.runsHeading}</h3>
      {status === 'unavailable' ? (
        <InlineError message={copy.runsUnavailable} onRetry={() => void load(null)} />
      ) : runs.length === 0 ? (
        <p className="pc-tracker-note">{copy.runsEmpty}</p>
      ) : (
        <ul>
          {runs.map((run) => {
            const detail = details[run.id];
            const troubled = run.outcome !== 'succeeded';
            return (
              <li key={run.id} data-testid="app-run" data-outcome={run.outcome}>
                <div className="pc-app-run-line">
                  <span className="pc-app-run-dot" data-outcome={run.outcome} aria-hidden="true" />
                  <span className="pc-app-run-text">
                    <span className="pc-app-run-outcome" data-testid="app-run-outcome">
                      {copy[OUTCOME_COPY[run.outcome]]}
                    </span>
                    <span className="pc-app-run-meta">
                      {[
                        copy[TRIGGER_COPY[run.trigger]],
                        formatMoment(run.finishedAt, locale),
                        run.durationMs === null ? null : seconds(run.durationMs),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  {troubled && (
                    <span className="pc-app-run-actions">
                      <button
                        type="button"
                        className="pc-app-run-toggle"
                        data-testid="app-run-log"
                        aria-expanded={Boolean(open[run.id])}
                        aria-controls={`app-run-log-${run.id}`}
                        onClick={() => void toggle(run)}
                      >
                        {open[run.id] ? copy.runHideLog : copy.runShowLog}
                        <ChevronDownIcon aria-hidden="true" />
                      </button>
                      {fixTo && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          data-testid="app-run-fix"
                          disabled={detail === 'loading'}
                          onClick={() => void fix(run)}
                        >
                          <MessageSquareIcon aria-hidden="true" />
                          {copy.runFix}
                        </Button>
                      )}
                    </span>
                  )}
                </div>
                {open[run.id] && <RunLog id={`app-run-log-${run.id}`} detail={detail} />}
              </li>
            );
          })}
        </ul>
      )}
      {next && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="app-runs-more"
          onClick={() => void load(next)}
        >
          {copy.runsMore}
        </Button>
      )}
      {fixFailed && (
        <p role="status" className="pc-tracker-note pc-app-runs-problem">
          {copy.runFixFailed}
        </p>
      )}
    </section>
  );
}

function RunLog({ id, detail }: { id: string; detail: Detail | undefined }) {
  const copy = useNamespaceCopy(appTrackerCopy);
  if (!detail || detail === 'loading')
    return <p id={id} role="status" className="pc-tracker-note pc-app-run-log" />;
  if (detail === 'failed')
    return (
      <p id={id} className="pc-tracker-note pc-app-run-log">
        {copy.runLogUnavailable}
      </p>
    );
  let input: string | null = null;
  if (detail.input !== null && detail.input !== undefined) {
    try {
      input = JSON.stringify(detail.input, null, 2);
    } catch {
      input = null;
    }
  }
  return (
    <div id={id} className="pc-app-run-log" data-testid="app-run-detail">
      {detail.error && (
        <>
          <h5>{copy.runError}</h5>
          <pre data-testid="app-run-error">
            {[`${detail.error.name}: ${detail.error.message}`, ...detail.error.stack].join('\n')}
          </pre>
        </>
      )}
      <h5>{copy.runPrinted}</h5>
      {detail.logs.lines.length === 0 ? (
        <p className="pc-tracker-note">{copy.runLogEmpty}</p>
      ) : (
        <pre data-testid="app-run-lines">
          {detail.logs.lines.map((line) => `[${line.level}] ${line.text}`).join('\n')}
        </pre>
      )}
      {detail.logs.dropped > 0 && (
        <p className="pc-tracker-note">
          {formatCopy(copy.runLogDropped, { n: String(detail.logs.dropped) })}
        </p>
      )}
      {input && (
        <>
          <h5>{copy.runInput}</h5>
          <pre data-testid="app-run-input">{input}</pre>
        </>
      )}
    </div>
  );
}
